<template>
  <div class="page page--fill dashboard">
    <div class="page-header">
      <div>
        <h1>Bonjour {{ currentUser.first_name }}</h1>
        <p>Vue d'ensemble de la plateforme, en direct sur l'API {{ $env.label }}.</p>
      </div>
      <div class="dashboard__links">
        <v-btn
          v-for="link in links"
          :key="link.route"
          :to="{ name: link.route }"
          outlined
          small>
          <v-icon
            left
            small>
            {{ link.icon }}
          </v-icon>
          {{ link.label }}
        </v-btn>
      </div>
    </div>

    <div class="dashboard__kpis">
      <router-link
        v-for="kpi in kpis"
        :key="kpi.key"
        :to="kpi.to"
        class="kpi dashboard__kpi">
        <div class="kpi__label">
          {{ kpi.label }}
        </div>
        <div class="kpi__value">
          {{ format(counts[kpi.key]) }}
        </div>
        <div class="kpi__hint">
          {{ kpi.hint(counts) }}
        </div>
      </router-link>
    </div>

    <div class="dashboard__grid">
      <div class="panel">
        <div class="panel__title">
          Activité
        </div>
        <div class="panel__body">
          <router-link
            v-for="line in lines"
            :key="line.key"
            :to="line.to"
            class="dashboard__line">
            <span>
              {{ line.label }}
              <span class="cell-muted">{{ line.hint }}</span>
            </span>
            <strong>{{ format(counts[line.key]) }}</strong>
          </router-link>
        </div>
      </div>

      <router-link
        :to="{ name: 'stats' }"
        class="panel dashboard__chart">
        <div class="panel__title">
          Ventes actées par mois
        </div>
        <div class="panel__body chart">
          <div
            v-for="month in months"
            :key="month.key"
            class="chart__col"
            :title="`${month.label} : ${format(month.count)} vente(s) actée(s)`">
            <span class="chart__value">{{ month.count === undefined ? '' : month.count }}</span>
            <div class="chart__track">
              <div
                class="chart__bar"
                :style="{ height: barHeight(month.count) }" />
            </div>
            <span class="chart__label">{{ month.label }}</span>
          </div>
        </div>
      </router-link>
    </div>

    <div
      ref="last"
      class="panel dashboard__last">
      <div class="panel__title">
        Dernières ventes
      </div>
      <div class="panel__body">
        <v-simple-table dense>
          <template #default>
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Statut</th>
                <th>Programme / lot</th>
                <th>Promoteur</th>
                <th>Créée le</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="lastReservations === null">
                <td
                  colspan="5"
                  class="cell-muted">
                  Chargement…
                </td>
              </tr>
              <tr
                v-for="item in lastReservations.slice(0, maxRows)"
                :key="item.pk">
                <td class="cell-mono">
                  {{ item.ref }}
                </td>
                <td>
                  <span :class="pillClass(RESERVATION_STATUS, item.status)">{{ labelOf(RESERVATION_STATUS, item.status) }}</span>
                </td>
                <td>
                  <span class="cell-strong">{{ item.program && item.program.name }}</span>
                  <span class="cell-muted"> · lot {{ item.lot && item.lot.ref }}</span>
                </td>
                <td>
                  <router-link
                    v-if="item.promoteur"
                    class="cell-link"
                    :to="{ name: 'accountVue', params: { id: item.promoteur.pk } }">
                    {{ item.promoteur.name }}
                  </router-link>
                </td>
                <td>{{ formatDate(item.created_on) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import { mapState } from 'vuex'
  import RESOURCES from '@batimentb/vadimm-js-api/resources'
  import { RESERVATION_STATUS, pillClass, labelOf, formatDate } from '../labels'

  const monthStart = moment().startOf('month').toISOString()

  // Chaque compteur = count de l'API sur une ressource filtrée
  const COUNTS = {
    accounts: { resource: RESOURCES.ACCOUNT },
    promoteurs: { resource: RESOURCES.ACCOUNT, params: { account_type: 'promoteur' } },
    commercials: { resource: RESOURCES.ACCOUNT, params: { account_type: 'commercial' } },
    users: { resource: RESOURCES.USER },
    programs: { resource: RESOURCES.PROGRAM },
    reservations: { resource: RESOURCES.RESERVATION },
    // Mêmes dates que la page Statistiques : signature pour les réservées, vente pour les vendues
    reservedMonth: { resource: RESOURCES.RESERVATION, params: { status: 'reserved', signature_date__gte: monthStart } },
    soldMonth: { resource: RESOURCES.RESERVATION, params: { status: 'sold', sale_date__gte: monthStart } },
    preReserved: { resource: RESOURCES.RESERVATION, params: { status: 'pre_reserved' } },
    options: { resource: RESOURCES.OPTION, params: { status: ['approved', 'waiting_list'] } },
    lotsAvailable: { resource: RESOURCES.LOT, params: { status: 'available' } }
  }

  export default {
    name: 'Dashboard',
    data () {
      return {
        RESERVATION_STATUS,
        counts: {},
        lastReservations: null,
        // Nombre de lignes affichées : ce qui tient dans la hauteur de l'écran, sans scroll
        maxRows: 4,
        kpis: [
          { key: 'accounts', label: 'Comptes', to: { name: 'accounts' }, hint: c => `${this.format(c.promoteurs)} promoteurs · ${this.format(c.commercials)} commercialisateurs` },
          { key: 'users', label: 'Utilisateurs', to: { name: 'users' }, hint: () => 'Tous profils' },
          { key: 'programs', label: 'Programmes', to: { name: 'programs' }, hint: () => 'Tous promoteurs' },
          { key: 'reservations', label: 'Ventes', to: { name: 'reservations' }, hint: () => 'Hors ventes en édition' }
        ],
        months: [],
        lines: [
          { key: 'reservedMonth', label: 'Réservations', hint: 'signées ce mois', to: { name: 'reservations', query: { status: 'reserved' } } },
          { key: 'soldMonth', label: 'Ventes', hint: 'actées ce mois', to: { name: 'reservations', query: { status: 'sold' } } },
          { key: 'preReserved', label: 'Pré-réservations', hint: 'en cours', to: { name: 'reservations', query: { status: 'pre_reserved' } } },
          { key: 'options', label: 'Options', hint: 'validées ou en attente', to: { name: 'lots' } },
          { key: 'lotsAvailable', label: 'Lots disponibles', to: { name: 'lots', query: { status: 'available' } } }
        ],
        links: [
          { label: 'Nouveau compte', icon: 'mdi-plus', route: 'newAccount' },
          { label: 'Bandeau', icon: 'mdi-bullhorn-outline', route: 'banner' },
          { label: 'Statistiques', icon: 'mdi-chart-line', route: 'stats' }
        ]
      }
    },
    computed: {
      ...mapState(['currentUser'])
    },
    created () {
      this.fetchCounts()
      this.fetchLastReservations()
      this.fetchMonths()
    },
    mounted () {
      window.addEventListener('resize', this.fitRows)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.fitRows)
    },
    methods: {
      pillClass,
      labelOf,
      formatDate,
      fetchCounts () {
        Object.entries(COUNTS).forEach(async ([key, { resource, params }]) => {
          try {
            const { data } = await this.$api.axios.get(resource, { params: { ...params, page: 1, per_page: 1 } })
            this.$set(this.counts, key, data.count || 0)
          } catch (e) {
            this.$set(this.counts, key, null)
          }
        })
      },
      async fetchLastReservations () {
        try {
          const { data } = await this.$api.axios.get(RESOURCES.RESERVATION, { params: { page: 1, per_page: 20, sort: 'created_on:desc' } })
          this.lastReservations = data.items || []
        } catch (e) {
          this.lastReservations = []
        }
        this.$nextTick(this.fitRows)
      },
      fitRows () {
        const panel = this.$refs.last
        if (!panel) return
        const head = panel.querySelector('thead tr')
        const row = panel.querySelector('tbody tr')
        const table = panel.querySelector('table')
        if (!head || !row || !table) return
        // hauteur libre du panneau = hauteur du panneau - tout ce qui est au-dessus des lignes - marge du bas
        const free = panel.getBoundingClientRect().bottom - head.getBoundingClientRect().bottom - 22
        this.maxRows = Math.max(1, Math.floor(free / row.getBoundingClientRect().height))
      },
      // Même calcul que la page Statistiques : ventes vendues par mois de date de vente
      fetchMonths () {
        const months = []
        for (let i = 11; i >= 0; i--) {
          const start = moment().subtract(i, 'months').startOf('month')
          months.push({ key: start.format('YYYY-MM'), label: start.format('MMM YY'), start, count: undefined })
        }
        this.months = months
        months.forEach(async month => {
          try {
            const { data } = await this.$api.axios.get(RESOURCES.RESERVATION, {
              params: {
                page: 1,
                per_page: 1,
                status: 'sold',
                sale_date__gte: month.start.toISOString(),
                sale_date__lt: month.start.clone().add(1, 'month').toISOString()
              }
            })
            month.count = data.count
          } catch (e) {
            month.count = null
          }
        })
      },
      barHeight (count) {
        const max = Math.max(1, ...this.months.map(m => m.count || 0))
        return `${Math.round((count || 0) / max * 100)}%`
      },
      format (count) {
        if (count === undefined) return '…'
        if (count === null) return '—'
        return count.toLocaleString('fr-FR')
      }
    }
  }
</script>

<style scoped>
  .dashboard__links {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .dashboard__kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }
  /* Mêmes dimensions que les chiffres clés des listes (KpiRow) */
  .dashboard__kpi {
    padding: 14px 18px;
    color: inherit !important;
    text-decoration: none;
  }
  .dashboard__kpi .kpi__value {
    font-size: 24px;
  }
  .dashboard__kpi:hover {
    background: #f4f4f5;
  }
  .dashboard__grid {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) 2fr;
    gap: 12px;
    margin-bottom: 12px;
  }
  .dashboard__line {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 5px 0;
    border-bottom: 1px solid #f0f0f2;
    color: inherit !important;
    text-decoration: none;
    font-size: 13.5px;
  }
  .dashboard__line:last-child {
    border-bottom: 0;
  }
  .dashboard__line:hover strong {
    text-decoration: underline;
  }
  .dashboard__line strong {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.5px;
  }
  .dashboard__chart {
    display: flex;
    flex-direction: column;
    color: inherit !important;
    text-decoration: none;
  }
  .dashboard__chart:hover {
    background: #f4f4f5;
  }
  /* Graphique repris de la page Statistiques */
  .chart {
    flex: 1;
    display: flex;
    align-items: flex-end;
    gap: 8px;
    min-height: 140px;
  }
  .chart__col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    gap: 4px;
  }
  .chart__value {
    font-size: 12px;
    font-weight: 600;
    color: #2b2b31;
    height: 16px;
  }
  .chart__track {
    flex: 1;
    width: 100%;
    max-width: 28px;
    display: flex;
    align-items: flex-end;
  }
  .chart__bar {
    width: 100%;
    min-height: 2px;
    background: rgba(17, 17, 19, 0.12);
    border-radius: 6px 6px 2px 2px;
    transition: height 0.3s, background 0.15s;
  }
  .chart__col:hover .chart__bar,
  .chart__col:last-child .chart__bar {
    background: #111113;
  }
  .chart__label {
    font-size: 11.5px;
    color: #71717a;
    white-space: nowrap;
  }
  .dashboard__last {
    flex: 1 1 auto !important;
    min-height: 0;
    overflow: hidden;
  }
  .dashboard__last .panel__body {
    padding-top: 6px;
  }
  .dashboard__last td {
    white-space: nowrap;
  }
  @media (max-width: 900px) {
    .dashboard__kpis {
      grid-template-columns: repeat(2, 1fr);
    }
    .dashboard__grid {
      grid-template-columns: 1fr;
    }
  }
</style>
